import * as React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { CancelledEventDetails } from '@/components/events/event-cancellation'
import { EventChapterPicker } from '@/components/events/event-chapter-picker'
import { EventDetailsForm, type EventDetails } from '@/components/events/event-details-form'
import { eventApiMessage } from '@/components/events/event-errors'
import { EventPreviousEdition } from '@/components/events/event-previous-edition'
import { EventRegistrationForm } from '@/components/events/event-registration-form'
import { EventRegistrationQuestions } from '@/components/events/event-registration-questions'
import { EventRevenueDestinations } from '@/components/events/event-revenue-destinations'
import { EventTicketTypes, type TaxRateSummary } from '@/components/events/event-ticket-types'
import { deleteJSON, getJSON, patchJSON } from '@/lib/api'
import type { EventRecord } from '@/pages/event-types'

/** The Settings tab: the event's detail form, tickets, questions and registration settings. */
export function EventSettings({
  event,
  viewOnly,
  taxRates,
  taxRatesState,
  onDeleted,
}: {
  event: EventRecord
  viewOnly: boolean
  taxRates: TaxRateSummary[]
  taxRatesState: 'loading' | 'error' | 'ready'
  onDeleted: () => void
}) {
  const owned = isOwnedEvent(event)
  return (
    <div className="max-w-3xl space-y-6">
      {!viewOnly && owned && <OwnedEventChapterPicker event={event} />}
      {(viewOnly || owned) && <EventPreviousEdition key={event.id} event={event} />}
      {event.status === 'cancelled' ? (
        <CancelledEventDetails event={event} />
      ) : (
        !viewOnly &&
        !owned && (
          <>
            <EventDetailsForm event={eventDetails(event)} />
            <EventRevenueDestinations
              eventId={event.id}
              tickets={event.ticket_types}
              eventDestination={event.revenue_destination}
            />
            <EventTicketTypes
              eventId={event.id}
              tickets={event.ticket_types}
              taxRates={taxRates}
              taxRatesState={taxRatesState}
            />
            <EventRegistrationQuestions
              eventId={event.id}
              questions={event.registration_questions ?? []}
            />
            <EventRegistrationForm event={event} />
          </>
        )
      )}
      {!viewOnly && event.status === 'draft' && !owned && (
        <DeleteDraftControl eventId={event.id} onDeleted={onDeleted} />
      )}
    </div>
  )
}

export function isOwnedEvent(record: EventRecord) {
  return Boolean(record.owner && typeof record.owner !== 'string' && record.owner.connection)
}

function OwnedEventChapterPicker({ event }: { event: EventRecord }) {
  const queryClient = useQueryClient()
  const [chapterId, setChapterId] = React.useState<number | null>(event.chapter?.id ?? null)
  const chaptersQuery = useQuery({
    queryKey: ['event-chapters'],
    queryFn: () => getJSON<{ chapters: { id: number; name: string }[] }>('/api/v1/chapters'),
    retry: false,
  })
  const update = useMutation({
    mutationFn: (nextChapterId: number | null) =>
      patchJSON<{ event: EventRecord }>(`/api/v1/events/${event.id}`, {
        event: { chapter_id: nextChapterId },
      }),
    onSuccess: ({ event: updated }) => {
      queryClient.setQueryData(['event', event.id], { event: updated })
      queryClient.invalidateQueries({ queryKey: ['events'] })
    },
  })

  if (event.chapter === undefined || chaptersQuery.isLoading || chaptersQuery.isError) return null

  return (
    <div className="rounded-md border border-border bg-card px-4 py-3">
      <EventChapterPicker
        chapters={chaptersQuery.data?.chapters ?? []}
        value={chapterId}
        onChange={(next) => {
          setChapterId(next)
          update.mutate(next)
        }}
      />
    </div>
  )
}

function DeleteDraftControl({ eventId, onDeleted }: { eventId: number; onDeleted: () => void }) {
  const [open, setOpen] = React.useState(false)
  const remove = useMutation({
    mutationFn: () => deleteJSON(`/api/v1/events/${eventId}`),
    onSuccess: onDeleted,
  })
  return (
    <>
      {remove.error && <Alert>{eventApiMessage(remove.error)}</Alert>}
      <Button type="button" variant="quietDestructive" onClick={() => setOpen(true)}>
        Delete draft
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete draft?</DialogTitle>
            <DialogDescription>
              This removes the Event and its Ticket Types. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Keep draft
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={remove.isPending}
              onClick={() => remove.mutate()}
            >
              {remove.isPending ? 'Deleting…' : 'Delete draft'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

function eventDetails(event: EventRecord): EventDetails {
  return { ...event, starts_at_local: event.starts_at_local ?? '' }
}
